import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import type { PricingFaq } from '@/_types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ---------------------------------------------------------------------------
// FAQ Form Dialog (Add / Edit)
// ---------------------------------------------------------------------------

interface FaqFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { question: string; answer: string; is_active: boolean }) => Promise<void>;
  initial?: Pick<PricingFaq, 'question' | 'answer' | 'is_active'>;
  title: string;
}

function FaqFormDialog({ open, onClose, onSave, initial, title }: FaqFormDialogProps) {
  const [question, setQuestion] = useState(initial?.question ?? '');
  const [answer, setAnswer] = useState(initial?.answer ?? '');
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [saving, setSaving] = useState(false);

  // Reset form fields when dialog opens with new initial values
  const handleOpenChange = (o: boolean) => {
    if (!o) onClose();
  };

  // Sync state when dialog re-opens with different initial data
  const resetToInitial = () => {
    setQuestion(initial?.question ?? '');
    setAnswer(initial?.answer ?? '');
    setIsActive(initial?.is_active ?? true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) { toast.error('Question is required'); return; }
    if (!answer.trim()) { toast.error('Answer is required'); return; }
    setSaving(true);
    try {
      await onSave({ question: question.trim(), answer: answer.trim(), is_active: isActive });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) resetToInitial();
        handleOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="faq-question">Question</Label>
            <Input
              id="faq-question"
              placeholder="e.g. Can I switch plans at any time?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="faq-answer">Answer</Label>
            <textarea
              id="faq-answer"
              placeholder="Write the answer here…"
              rows={5}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              className="w-full text-sm rounded-md border border-input bg-background px-3 py-2 resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select
              value={isActive ? 'active' : 'inactive'}
              onValueChange={(v) => setIsActive(v === 'active')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save FAQ'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// FAQ card row (display only — edit/delete via buttons)
// ---------------------------------------------------------------------------

function FaqCard({
  faq,
  onEdit,
  onDelete,
  onToggleActive,
}: {
  faq: PricingFaq;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
}) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{faq.question}</p>
          <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{faq.answer}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
          <button
            type="button"
            onClick={onToggleActive}
            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
              faq.is_active
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {faq.is_active ? 'Active' : 'Inactive'}
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FaqsPage
// ---------------------------------------------------------------------------

export default function FaqsPage() {
  const qc = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [editFaq, setEditFaq] = useState<PricingFaq | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-pricing-faqs'],
    queryFn: async () => {
      const res = await apiClient.get('/pricing/faqs');
      return res.data.data.faqs as PricingFaq[];
    },
  });

  const faqs: PricingFaq[] = data ?? [];

  const handleAdd = async (payload: { question: string; answer: string; is_active: boolean }) => {
    await apiClient.post('/pricing/faqs', {
      ...payload,
      sort_order: faqs.length + 1,
    });
    qc.invalidateQueries({ queryKey: ['admin-pricing-faqs'] });
    toast.success('FAQ added');
  };

  const handleUpdate = async (faqId: string, payload: Partial<PricingFaq>) => {
    await apiClient.patch(`/pricing/faqs/${faqId}`, payload);
    qc.invalidateQueries({ queryKey: ['admin-pricing-faqs'] });
    toast.success('FAQ updated');
  };

  const handleDelete = async (faqId: string) => {
    try {
      await apiClient.delete(`/pricing/faqs/${faqId}`);
      qc.invalidateQueries({ queryKey: ['admin-pricing-faqs'] });
      toast.success('FAQ deleted');
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Pricing FAQs</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Global — applies to all plans on the pricing page
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" />
          Add FAQ
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : faqs.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-card p-12 text-center">
          <p className="text-muted-foreground text-sm">No FAQs yet. Click "Add FAQ" to create the first one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {faqs.map((faq) => (
            <FaqCard
              key={faq.id}
              faq={faq}
              onEdit={() => setEditFaq(faq)}
              onDelete={() => setDeleteId(faq.id)}
              onToggleActive={() => handleUpdate(faq.id, { is_active: !faq.is_active })}
            />
          ))}
        </div>
      )}

      {/* Add dialog */}
      {addOpen && (
        <FaqFormDialog
          open={addOpen}
          title="Add FAQ"
          onClose={() => setAddOpen(false)}
          onSave={handleAdd}
        />
      )}

      {/* Edit dialog */}
      {editFaq && (
        <FaqFormDialog
          open={!!editFaq}
          title="Edit FAQ"
          initial={editFaq}
          onClose={() => setEditFaq(null)}
          onSave={(payload) => handleUpdate(editFaq.id, payload)}
        />
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete FAQ?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the FAQ. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && handleDelete(deleteId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
